/** The deck: **Next** and **Asked** (UX §7.4, §7.5, §8.4).
 *
 * This is the owner's first and fourth complaints answered — the queue moved out of a fixed
 * 320 px aside into the main column, at a size a teacher can read while standing. The plan is
 * not a separate list any more: it is the *shape* of this queue, its segments printed as the
 * headers, and when the plan runs out a divider says so and the bank follows.
 */
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import type { Item } from "../teacher/api";
import { Button } from "../ui";
import type { AskedEntry, Channels } from "./useLive";
import { AskedRow } from "./AskedRow";
import { QueueCard } from "./QueueCard";
import { unsupportedFor } from "./channels";
import type { Deck } from "./queue";

type Props = {
  deck: Deck;
  tab: "next" | "asked";
  onTab: (tab: "next" | "asked") => void;
  selected: string | null;
  onSelect: (itemId: string) => void;
  loCode: (loId: string | null | undefined) => string | null;
  /** Why nothing may be asked from the deck — an i18n key — or null. */
  blocked: string | null;
  /** The session has ended: the deck is a record of the lesson, not a way to run it. */
  ended: boolean;
  busyItemId: string | null;
  released: boolean;
  search: string;
  onSearch: (value: string) => void;
  onAsk: (item: Item) => void;
  onSkip: (itemId: string) => void;
  onRestore: (itemId: string) => void;
  onRevote: (entry: AskedEntry) => void;
  onAskAgain: (entry: AskedEntry) => void;
  onScore: (entry: AskedEntry) => void;
  onKeep: (entry: AskedEntry) => void;
  canRevote: (entry: AskedEntry) => boolean;
  kept: string[];
  keeping: string | null;
  /** The staff frame's channel block: a card whose type a channel cannot carry is marked
   * only when the course has students on that channel (FR-054). */
  channels?: Channels;
};

export function QuestionDeck(p: Props) {
  const { t } = useTranslation();
  const { deck, tab } = p;
  const remaining = deck.next.length;

  return (
    <section className="flex flex-col gap-3" aria-label={t("live.deck.label")}>
      <div role="tablist" aria-label={t("live.deck.label")} className="flex gap-2">
        {(["next", "asked"] as const).map((id) => (
          <button
            key={id}
            role="tab"
            type="button"
            id={`deck-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`deck-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => p.onTab(id)}
            className={[
              "min-h-[var(--control-h)] rounded-[var(--radius-control)] px-4 font-semibold",
              tab === id ? "bg-[color:var(--accent)] text-[color:var(--accent-ink)]" : "border border-[color:var(--line)]",
            ].join(" ")}
          >
            {id === "next" ? t("live.deck.next") : t("live.deck.asked")} ({id === "next" ? remaining : deck.asked.length})
          </button>
        ))}
      </div>

      {tab === "next" ? (
        <div role="tabpanel" id="deck-panel-next" aria-labelledby="deck-tab-next" className="flex flex-col gap-3">
          {remaining > 8 && (
            <input
              className="field w-full"
              type="search"
              value={p.search}
              aria-label={t("live.deck.search")}
              placeholder={t("live.deck.search")}
              onChange={(e) => p.onSearch(e.target.value)}
            />
          )}
          {p.ended && remaining > 0 && <p className="text-[color:var(--ink-2)]">{t("live.deck.endedNext")}</p>}
          {remaining === 0 && <p className="text-[color:var(--ink-2)]">{t("live.deck.emptyNext")}</p>}
          <ul className="flex flex-col gap-3">
            {deck.next.map((row) => {
              const facts = row.segment !== null ? deck.segments[row.segment] : null;
              return (
                <Fragment key={row.item.item_id}>
                  {row.first && facts && (
                    <li className="pt-1 text-sm font-semibold">
                      {t("live.deck.segment", { n: facts.index + 1, title: facts.title, minutes: facts.minutes })}
                      <span className="ml-2 font-normal text-[color:var(--ink-2)]">
                        {t("live.deck.progress", { a: facts.asked, n: facts.total })}
                      </span>
                    </li>
                  )}
                  {row.divider && (
                    <li className="border-t border-dashed pt-2 text-sm text-[color:var(--ink-2)]">{t("live.deck.planDone")}</li>
                  )}
                  <QueueCard
                    item={row.item}
                    lo={p.loCode(row.item.lo_id)}
                    selected={p.selected === row.item.item_id}
                    blocked={p.blocked}
                    ended={p.ended}
                    busy={p.busyItemId === row.item.item_id}
                    tabIndex={p.selected === row.item.item_id ? 0 : -1}
                    onFocus={() => p.onSelect(row.item.item_id)}
                    onAsk={() => p.onAsk(row.item)}
                    onSkip={() => p.onSkip(row.item.item_id)}
                    // The channel marker, before the teacher picks the card: the mirrored
                    // table in useLive.ts says what a channel cannot carry, the frame says
                    // whether anyone in this course is on it. A course with no such students
                    // shows no marker and no empty column. Opening the card is not blocked.
                    // A printed card and a clicker have their own sentences: their students
                    // have no browser to fall back on, and the name does not slot into the
                    // Telegram wording. The clicker's appears once the course has a clicker
                    // join on record, not as soon as the mirrored table lists it.
                    note={unsupportedFor(p.channels, row.item).map((u) => (
                      <p key={u.channel} className="text-sm text-[color:var(--ink-2)]" data-deck-channel={u.channel}>
                        {u.channel === "card"
                          ? t("live.channel.printed.unsupported", { count: u.count })
                          : u.channel === "clicker"
                            ? t("live.channel.clicker.unsupported", { count: u.count })
                            : t("live.channel.cardUnsupported", { count: u.count, channel: t(`live.channel.names.${u.channel}`, { defaultValue: u.channel }) })}
                      </p>
                    ))}
                  />
                </Fragment>
              );
            })}
          </ul>
          {deck.skipped.length > 0 && (
            <details className="rounded-[var(--radius-card)] border border-dashed p-3">
              <summary className="cursor-pointer text-sm">{t("live.deck.skipped", { n: deck.skipped.length })}</summary>
              <ul className="mt-2 flex flex-col gap-2">
                {deck.skipped.map((item) => (
                  <li key={item.item_id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate">{item.stem}</span>
                    <Button variant="tertiary" onClick={() => p.onRestore(item.item_id)}>{t("live.deck.restore")}</Button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ) : (
        <div role="tabpanel" id="deck-panel-asked" aria-labelledby="deck-tab-asked" className="flex flex-col gap-3">
          {deck.asked.length === 0 && <p className="text-[color:var(--ink-2)]">{t("live.deck.emptyAsked")}</p>}
          <ul className="flex flex-col gap-3">
            {deck.asked.map((entry) => (
              <AskedRow
                key={`${entry.item_id}:${entry.attempt}`}
                entry={entry}
                lo={p.loCode(entry.lo_id)}
                canRevote={p.canRevote(entry)}
                blocked={p.blocked}
                ended={p.ended}
                released={p.released}
                kept={p.kept.includes(entry.item_id)}
                keeping={p.keeping === entry.item_id}
                onRevote={() => p.onRevote(entry)}
                onAskAgain={() => p.onAskAgain(entry)}
                onScore={() => p.onScore(entry)}
                onKeep={() => p.onKeep(entry)}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
